import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { getLscArchiveItem, listLscArchive, listLscMatches, listLscMembers, unpublishLscArchiveItem, LscApiError } from '../../api';
import { nflCanonicalName, resolveTeamVisual } from '../../cfbBranding/teamVisualResolver';
import type { LscArchiveItem, LscLeague, LscMatch, LscMember, LscVodCandidate } from '../../types';
import { lscPath } from '../../routing';
import { useLeague } from '../../hooks/useLeague';
import { parseLscMutationError, type ParsedLscMutationError } from '../../entitlementBillingErrors';
import ArchiveCopyLinkButton from '../ArchiveCopyLinkButton';
import ArchiveWatchVodSection from '../ArchiveWatchVodSection';
import ArchiveStatusBadge from '../ArchiveStatusBadge';
import CfbMatchupChips from '../CfbMatchupChips';
import ErrorBanner from '../ErrorBanner';
import LoadingState from '../LoadingState';
import LscMutationErrorAlert from '../LscMutationErrorAlert';
import UnpublishArchiveConfirmModal from '../UnpublishArchiveConfirmModal';
import { publishSourceLabel } from '../../stream/publishSourceLabel';
import { finalScore } from '../../stream/streamGamesModel';
import { vodExpiryLabel } from '../../stream/vodExpiryLabel';
import {
  broadcastCaption,
  broadcastSources,
  expiryBannerCopy,
  moreFromSameWeek,
  replayKicker,
  resolveBroadcastSide,
  withKnownDisplayName,
  type BroadcasterMember,
} from '../../stream/watchPageCopy';
import { broadcasterDisplayName } from '../../stream/broadcasterName';
import { formatReplayDuration } from '../../stream/replayDuration';
import { carryStreamSearch, isMemberPreview } from '../../stream/streamPages';
import MatchupTile, { type MatchupTileSide } from './MatchupTile';
import { StreamSurfaceChrome } from './StreamSurfaceChrome';
import Rail from './Rail';

export type StreamWatchViewProps = {
  leagueId: string;
  archive: LscArchiveItem;
  league: Pick<LscLeague, 'game_key' | 'active_edition' | 'stream_pages' | 'can_set_stream_pages'> | null;
  isCommissioner: boolean;
  cards: LscArchiveItem[];
  score?: { away: number; home: number } | null;
  members?: BroadcasterMember[];
  now?: Date;
  error?: ParsedLscMutationError | null;
  onDismissError?: () => void;
  unpublishSuccess?: { message: string } | null;
  onUnpublish?: () => void;
  unpublishing?: boolean;
};

function formatPlayedDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDetailDate(value?: string | null): string {
  return formatPlayedDate(value) ?? '—';
}

function formatStreamSource(archive: LscArchiveItem): string {
  const provider = archive.stream_provider?.trim();
  if (provider) return provider.charAt(0).toUpperCase() + provider.slice(1);
  if (archive.embed_provider?.trim() === 'twitch') return 'Twitch';
  const embedProvider = archive.embed_provider?.trim();
  if (embedProvider) return embedProvider.charAt(0).toUpperCase() + embedProvider.slice(1);
  return '—';
}

function tileSide(label: string | null | undefined, gameKey?: string | null): MatchupTileSide {
  const name = label?.trim() || 'Team';
  const visual = resolveTeamVisual({ label: name, gameKey: gameKey || undefined });
  return {
    name: visual.displayName,
    abbreviation: visual.abbreviation,
    tileColors: visual.tileColors,
  };
}

function sideWord(side: 'away' | 'home'): string {
  return side === 'away' ? 'Away' : 'Home';
}

function watchOrigin(state: unknown): 'games' | 'archive' | 'home' {
  const from = (state as { watchFrom?: string } | null)?.watchFrom;
  if (from === 'archive' || from === 'home' || from === 'games') return from;
  return 'games';
}

function weekTitle(stage: string): string {
  return stage.toLowerCase().startsWith('week') ? `More from ${stage}` : `More from Week ${stage}`;
}

function watchTone(
  score: { away: number; home: number } | null,
  side: 'away' | 'home'
): 'win' | 'lose' | 'even' {
  if (!score || score.away === score.home) return 'even';
  const winning = score.away > score.home ? 'away' : 'home';
  return winning === side ? 'win' : 'lose';
}

function watchToneClass(tone: 'win' | 'lose' | 'even'): string {
  if (tone === 'win') return 'font-bold text-stream-ink';
  if (tone === 'lose') return 'font-medium text-stream-muted';
  return 'font-medium text-stream-ink';
}

export const StreamWatchView: React.FC<StreamWatchViewProps> = ({
  leagueId,
  archive,
  league,
  isCommissioner,
  cards,
  score = null,
  members = [],
  now = new Date(),
  error = null,
  onDismissError,
  unpublishSuccess = null,
  onUnpublish,
  unpublishing = false,
}) => {
  const location = useLocation();
  const gameKey = league?.game_key;
  const awayStored = archive.match_summary?.away_side_label?.trim() || 'Away';
  const homeStored = archive.match_summary?.home_side_label?.trim() || 'Home';
  const fullName = gameKey === 'cfb' ? (label: string) => label : nflCanonicalName;
  const awayLabel = fullName(awayStored) || awayStored;
  const homeLabel = fullName(homeStored) || homeStored;
  const origin = watchOrigin(location.state);
  const operatorView = isCommissioner && !isMemberPreview(location.search, { isOwner: false, isCommissioner });
  const streamViewer = { operator: isCommissioner };
  const streamQuery = carryStreamSearch(location.search, streamViewer);
  const homeQuery = carryStreamSearch(location.search, streamViewer, undefined, 'home');
  const back =
    origin === 'archive'
      ? { to: `${lscPath(`/leagues/${leagueId}/archive`)}${streamQuery}`, label: 'Back to Archive' }
      : origin === 'home'
        ? { to: `${lscPath(`/leagues/${leagueId}`)}${homeQuery}`, label: 'Back to Home' }
        : { to: `${lscPath(`/leagues/${leagueId}/matches`)}${streamQuery}`, label: 'Back to Games' };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [archive.id]);
  const sources = broadcastSources(archive, cards).map((source) => withKnownDisplayName(source, cards));
  const weekMates = moreFromSameWeek(archive, cards);
  const stage = archive.match_summary?.stage_label?.trim();
  const expiry = expiryBannerCopy(
    vodExpiryLabel(archive.vod_created_at, archive.playback_status, now)
  );
  const publishedBy = publishSourceLabel(archive.publish_source);
  const played = formatPlayedDate(archive.vod_created_at);
  const duration = formatReplayDuration(archive.duration_seconds);
  const onlyBroadcast = sources.length === 1 ? sources[0] : null;
  const onlyName = onlyBroadcast
    ? broadcasterDisplayName({
        login: onlyBroadcast.provider_broadcaster_login,
        twitchDisplayName: onlyBroadcast.provider_broadcaster_display_name,
        members,
      })
    : '';
  const onlySide = onlyBroadcast
    ? resolveBroadcastSide({
        login: onlyBroadcast.provider_broadcaster_login,
        storedSide: onlyBroadcast.broadcaster_side,
        awayLabel: awayStored,
        homeLabel: homeStored,
        members,
      })
    : null;
  const streamedBy = onlyName
    ? onlySide
      ? `Streamed by ${onlyName} (${onlySide})`
      : `Streamed by ${onlyName}`
    : null;
  const meta = [
    duration,
    played ? `Played ${played}` : null,
    streamedBy,
    publishedBy,
  ]
    .filter(Boolean)
    .join(' · ');

  const picker = sources.length > 1 ? (
    <div className="flex flex-col gap-2" data-testid="stream-watch-broadcasts">
      {sources.map((source) => {
        const watching = source.id === archive.id;
        const side = resolveBroadcastSide({
          login: source.provider_broadcaster_login,
          storedSide: source.broadcaster_side,
          awayLabel: awayStored,
          homeLabel: homeStored,
          members,
        });
        const teamLabel = side === 'away' ? awayStored : side === 'home' ? homeStored : '';
        const visual = teamLabel
          ? resolveTeamVisual({ label: teamLabel, gameKey: gameKey || undefined })
          : null;
        const login = source.provider_broadcaster_login?.trim();
        const name = broadcasterDisplayName({
          login,
          twitchDisplayName: source.provider_broadcaster_display_name,
          members,
        });
        const shown =
          name && login && name.toLowerCase() === login.toLowerCase() ? `@${login}` : name || 'Broadcaster';
        const body = (
          <>
            {visual ? (
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full font-stream-display text-xs font-bold"
                style={{
                  backgroundColor: visual.tileColors?.primary ?? '#222838',
                  color: visual.tileColors?.onPrimary ?? '#F2F4F8',
                }}
                data-testid="stream-watch-broadcast-mark"
                data-side={side ?? undefined}
              >
                {visual.abbreviation}
              </span>
            ) : null}
            <span>{shown}</span>
            {side ? <span className="text-stream-muted">{sideWord(side)}</span> : null}
            {watching ? <span data-testid="stream-watch-current">Watching</span> : null}
          </>
        );
        if (watching) {
          return (
            <div
              key={source.id}
              className="flex min-h-11 items-center gap-3 rounded-stream-md bg-stream-raised px-4"
              data-testid="stream-watch-broadcast"
              data-watching="true"
            >
              {body}
            </div>
          );
        }
        return (
          <Link
            key={source.id}
            to={`${lscPath(
              `/leagues/${leagueId}/archive/${encodeURIComponent(source.id)}`
            )}${streamQuery}`}
            state={location.state}
            className="flex min-h-11 items-center gap-3 rounded-stream-md bg-stream-raised px-4"
            data-testid="stream-watch-broadcast"
          >
            {body}
          </Link>
        );
      })}
    </div>
  ) : null;

  return (
    <div
      className="stream-scope flex flex-col gap-6 overflow-x-hidden bg-stream-ground font-stream-body text-stream-ink"
      data-testid="stream-watch-page"
    >
      <StreamSurfaceChrome
        leagueId={leagueId}
        streamPages={league?.stream_pages}
        canSet={league?.can_set_stream_pages}
        isOperator={isCommissioner}
      />
      <Link
        to={back.to}
        className="font-stream-body text-sm text-stream-muted"
        data-testid="archive-detail-back-link"
      >
        {`← ${back.label}`}
      </Link>

      {expiry ? (
        <p
          className="rounded-stream-md border border-amber-600 bg-amber-950/40 px-4 py-3 text-sm text-amber-100"
          data-testid="stream-watch-expiry"
        >
          {expiry}
        </p>
      ) : null}

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start max-w-lsc-watch" data-testid="stream-watch-stage">
        <div className="flex w-full flex-col gap-4 lg:w-[65%]" data-testid="stream-watch-primary">
          <ArchiveWatchVodSection archive={archive} leagueId={leagueId} playerOnly />
          <header className="flex flex-col gap-3">
            <p className="font-stream-body text-sm uppercase tracking-wide text-stream-muted" data-testid="stream-watch-kicker">
              {replayKicker(archive, league?.active_edition)}
            </p>
            <h1 className="font-stream-display text-4xl font-bold uppercase tracking-wide" data-testid="stream-watch-title">
              {awayStored} @ {homeStored}
            </h1>
            {meta ? (
              <p className="text-stream-muted" data-testid="stream-watch-meta">
                {meta}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid="stream-watch-utilities">
              {archive.url?.trim() ? (
                <a
                  href={archive.url.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-stream-ink"
                  data-testid="archive-vod-watch-on-twitch"
                >
                  Watch on Twitch
                </a>
              ) : null}
              {archive.url?.trim() ? <span className="text-stream-muted">·</span> : null}
              <ArchiveCopyLinkButton
                leagueId={leagueId}
                archiveId={archive.id}
                label="Copy link"
                showVisibilityReminder={false}
              />
            </div>
            <p className="text-sm text-stream-muted" data-testid="stream-watch-retention">
              Twitch may remove this video after a while.
            </p>
          </header>
        </div>
        <div className="flex w-full flex-col gap-4 lg:w-[35%]" data-testid="stream-watch-aside">
          {picker}
          <section className="flex flex-col gap-3" data-testid="stream-watch-matchup">
            <MatchupTile
              size="band"
              away={tileSide(archive.match_summary?.away_side_label, gameKey)}
              home={tileSide(archive.match_summary?.home_side_label, gameKey)}
            />
            <p
              data-testid="stream-watch-side-away"
              data-score-tone={watchTone(score, 'away')}
              className={watchToneClass(watchTone(score, 'away'))}
            >
              <span>Away</span>
              <span> · {awayLabel}</span>
            </p>
            <p
              data-testid="stream-watch-side-home"
              data-score-tone={watchTone(score, 'home')}
              className={watchToneClass(watchTone(score, 'home'))}
            >
              <span>Home</span>
              <span> · {homeLabel}</span>
            </p>
            {score ? (
              <p className="font-stream-display text-2xl font-medium" data-testid="stream-watch-score">
                <span className={watchToneClass(watchTone(score, 'away'))} data-winning={score.away > score.home ? 'true' : undefined} data-score-tone={watchTone(score, 'away')}>
                  {score.away}
                </span>
                <span className="px-2 text-stream-muted">–</span>
                <span className={watchToneClass(watchTone(score, 'home'))} data-winning={score.home > score.away ? 'true' : undefined} data-score-tone={watchTone(score, 'home')}>
                  {score.home}
                </span>
              </p>
            ) : null}
            {gameKey === 'cfb' && archive.match_summary?.away_side_label && archive.match_summary?.home_side_label ? (
              <CfbMatchupChips
                awayLabel={archive.match_summary.away_side_label}
                homeLabel={archive.match_summary.home_side_label}
                separator="@"
                size="sm"
                showLabels
              />
            ) : null}
          </section>
        </div>
      </div>

      <LscMutationErrorAlert error={error} onDismiss={onDismissError} />

      {unpublishSuccess ? (
        <div
          className="rounded-stream-md border border-green-700 bg-green-900/30 p-4 text-sm"
          data-testid="unpublish-success-banner"
        >
          <p className="text-green-100">{unpublishSuccess.message}</p>
          <div className="mt-3 flex flex-wrap gap-4">
            <Link to={lscPath(`/leagues/${leagueId}/archive`)} className="text-primary-300">
              Back to Archives
            </Link>
            <Link
              to={lscPath(`/leagues/${leagueId}/matches`)}
              className="text-primary-300"
              data-testid="unpublish-success-schedules-link"
            >
              Back to League Schedules
            </Link>
          </div>
        </div>
      ) : null}

      {operatorView ? (
      <div className="flex max-w-lsc-narrow flex-col gap-3" data-testid="archive-replay-details">
        <h2 className="font-stream-display text-xl font-bold uppercase tracking-wide">Replay details</h2>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-stream-muted">Broadcaster</dt>
            <dd>{archive.provider_broadcaster_login ? `@${archive.provider_broadcaster_login}` : '—'}</dd>
          </div>
          <div>
            <dt className="text-stream-muted">Published</dt>
            <dd>{formatDetailDate(archive.published_at)}</dd>
          </div>
          <div>
            <dt className="text-stream-muted">Source</dt>
            <dd>{formatStreamSource(archive)}</dd>
          </div>
          {operatorView ? (
            <div data-testid="stream-watch-status">
              <dt className="text-stream-muted">Status</dt>
              <dd>
                {archive.archive_status === 'published' ? (
                  'Published'
                ) : archive.archive_status ? (
                  <ArchiveStatusBadge status={archive.archive_status} />
                ) : (
                  '—'
                )}
              </dd>
            </div>
          ) : null}
          {operatorView ? (
            <div data-testid="stream-watch-visibility">
              <dt className="text-stream-muted">Visibility</dt>
              <dd className="capitalize">{(archive.visibility ?? 'league_members').replace(/_/g, ' ')}</dd>
            </div>
          ) : null}
        </dl>
        {operatorView && archive.archive_status === 'published' ? (
          <div data-testid="archive-commissioner-controls">
            <button
              type="button"
              className="btn-secondary border-red-800 text-red-300"
              onClick={onUnpublish}
              disabled={unpublishing}
              data-testid="unpublish-archive-button"
            >
              Unpublish VOD
            </button>
          </div>
        ) : null}
      </div>
      ) : null}

      {stage && weekMates.length > 0 ? (
        <Rail title={weekTitle(stage)} scrollerLabel={weekTitle(stage)}>
          {weekMates.map((card) => (
            <Link
              key={card.id}
              to={`${lscPath(`/leagues/${leagueId}/archive/${encodeURIComponent(card.id)}`)}${streamQuery}`}
              state={location.state}
              className="w-72 shrink-0"
              data-testid="stream-watch-more"
            >
              <MatchupTile
                size="rail"
                away={tileSide(card.match_summary?.away_side_label, gameKey)}
                home={tileSide(card.match_summary?.home_side_label, gameKey)}
              />
              <div className="mt-2 flex flex-col gap-0.5" data-testid="stream-tile-caption">
                {card.match_summary?.stage_label?.trim() ? (
                  <p className="text-xs text-stream-muted">{card.match_summary.stage_label.trim()}</p>
                ) : null}
                <p className="font-stream-display text-lg font-bold uppercase leading-tight">
                  {`${card.match_summary?.away_side_label?.trim() || 'Away'} @ ${card.match_summary?.home_side_label?.trim() || 'Home'}`}
                </p>
                {broadcastCaption(
                  (card.sources?.length ? card.sources : [card]).map((source) => ({
                    login: broadcasterDisplayName({
                      login: source.provider_broadcaster_login,
                      twitchDisplayName: source.provider_broadcaster_display_name,
                      members,
                    }),
                  }))
                ) ? (
                  <p className="text-sm text-stream-muted">
                    {broadcastCaption(
                      (card.sources?.length ? card.sources : [card]).map((source) => ({
                        login: broadcasterDisplayName({
                      login: source.provider_broadcaster_login,
                      twitchDisplayName: source.provider_broadcaster_display_name,
                      members,
                    }),
                      }))
                    )}
                  </p>
                ) : null}
              </div>
            </Link>
          ))}
        </Rail>
      ) : null}
    </div>
  );
};

const StreamWatchPage: React.FC<{ archiveId: string }> = ({ archiveId }) => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const location = useLocation();
  const memberPreview = new URLSearchParams(location.search).get('as') === 'member';
  const { league, isCommissioner } = useLeague();
  const [archive, setArchive] = useState<LscArchiveItem | null>(null);
  const [cards, setCards] = useState<LscArchiveItem[]>([]);
  const [schedule, setSchedule] = useState<LscMatch[]>([]);
  const [members, setMembers] = useState<LscMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<ParsedLscMutationError | null>(null);
  const [unpublishing, setUnpublishing] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [unpublishSuccess, setUnpublishSuccess] = useState<{
    message: string;
    candidate?: LscVodCandidate | null;
  } | null>(null);

  const load = useCallback(async () => {
    if (!leagueId || !archiveId) return;
    setLoading(true);
    setError(null);
    setUnavailable(false);
    try {
      const data = await getLscArchiveItem(leagueId, archiveId);
      setArchive(data);
    } catch (err) {
      if (err instanceof LscApiError && (err.status === 403 || err.status === 404)) {
        setUnavailable(true);
      } else {
        setError(parseLscMutationError(err, 'Could not load archive item.'));
      }
    } finally {
      setLoading(false);
    }
    try {
      const items = await listLscArchive(leagueId, {
        archive_status: 'published',
        group: 'match',
        limit: 24,
      });
      setCards(items);
    } catch {
      setCards([]);
    }
    try {
      const matchData = await listLscMatches(leagueId, { scope: 'active' });
      setSchedule(matchData);
    } catch {
      setSchedule([]);
    }
    try {
      const memberData = await listLscMembers(leagueId);
      setMembers(memberData.members);
    } catch {
      setMembers([]);
    }
  }, [leagueId, archiveId, memberPreview]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUnpublish = async (note: string) => {
    if (!leagueId || !archiveId) return;
    setUnpublishing(true);
    setError(null);
    try {
      const result = await unpublishLscArchiveItem(leagueId, archiveId, {
        note: note || undefined,
      });
      setUnpublishSuccess({
        message: result.message ?? 'VOD unpublished and returned to review.',
        candidate: result.candidate,
      });
      setUnpublishModalOpen(false);
      await load();
    } catch (err) {
      setError(parseLscMutationError(err, 'Unpublish failed.'));
    } finally {
      setUnpublishing(false);
    }
  };

  if (loading) return <LoadingState message="Loading archive..." />;

  if (unavailable) {
    return (
      <div className="space-y-4" data-testid="lsc-archive-unavailable">
        <h1 className="font-stream-display text-2xl font-bold uppercase">Archive</h1>
        <ErrorBanner message="This archive is not available." />
        <Link
          to={lscPath(`/leagues/${leagueId}/archive`)}
          className="text-sm text-stream-muted"
          data-testid="archive-detail-back-link"
        >
          ← Back to Archives
        </Link>
      </div>
    );
  }

  if (!archive) {
    return error ? <LscMutationErrorAlert error={error} /> : null;
  }

  const matched = schedule.find((match) => match.id === archive.competition_match_id)
    ?? schedule.find(
      (match) => (match.external_match_id ?? '') !== '' && match.external_match_id === archive.competition_match_id
    );

  return (
    <>
      <StreamWatchView
        leagueId={leagueId!}
        archive={archive}
        league={league}
        isCommissioner={isCommissioner}
        cards={cards}
        members={members}
        score={finalScore(matched)}
        error={error}
        onDismissError={() => setError(null)}
        unpublishSuccess={unpublishSuccess}
        onUnpublish={() => setUnpublishModalOpen(true)}
        unpublishing={unpublishing}
      />
      <UnpublishArchiveConfirmModal
        open={unpublishModalOpen}
        onClose={() => setUnpublishModalOpen(false)}
        onConfirm={handleUnpublish}
        unpublishing={unpublishing}
      />
    </>
  );
};

export default StreamWatchPage;
